import { useEffect, useState } from 'react';
import { getCsrfToken } from '../../utils/csrf';
import { ExpenseChanges, ExpenseData, ExpensesResponse, TrendData } from '../models/ModelExpenses';

// Define default categories that are commonly used
const DEFAULT_CATEGORIES = [
  'food', 'transport', 'entertainment', 'shopping', 
  'housing', 'utilities', 'health', 'education', 
  'travel', 'digital_services', 'clothing', 'others'
];

export function useExpenses() {
  const [expenseData, setExpenseData] = useState<ExpenseData[]>([]);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [totals, setTotals] = useState({
    total_expenses: 0,
    variable_expense: 0,
    fixed_expense: 0,
    subscription_expense: 0,
    installment_expense: 0,
    value_not_matched: 0,
    monthly_expenses: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExpenseData = async () => {
    const controller = new AbortController();
    
    try {
      setIsLoading(true);
      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/expenses/', {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'X-CSRFToken': csrfToken,
        }
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Check if we have the new API response format
        if (result.has_expense_data && result.data) {
          // Transform the data into the expected format
          const { 
            dates, 
            total_expenses, 
            fixed_expenses, 
            subscription_expenses, 
            variable_expenses, 
            installment_expenses,
            categories: apiCategories,
            category_values: apiCategoryValues
          } = result.data;
          
          // Use categories from API if available, otherwise use defaults
          const categories = apiCategories || DEFAULT_CATEGORIES;
          
          // Create category data structure
          const categoryData = {
            labels: categories,
            values: apiCategoryValues || dates.map(() => categories.map(() => 0))
          };
          
          // Create an array of expense objects from the arrays in the response
          const expenses: ExpenseData[] = dates.map((date: string, index: number) => ({
            date,
            total_expenses: total_expenses[index] || 0,
            fixed_expense: fixed_expenses[index] || 0,
            subscription_expense: subscription_expenses[index] || 0,
            variable_expense: variable_expenses[index] || 0,
            installment_expense: installment_expenses[index] || 0,
            value_not_matched: 0, // Default value if not provided
            monthly_expenses: total_expenses[index] || 0,
            categories: categoryData.labels, // Add categories
            category_values: apiCategoryValues ? 
              [apiCategoryValues[index]] : // Use API category values if available
              [categoryData.values[index]] // Otherwise use defaults
          }));
          
          // Sort expenses by date (newest first)
          const sortedExpenses = [...expenses].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getTime() - dateA.getTime();
          });
          
          // Create trend data from the arrays
          const trendData: TrendData = {
            labels: dates,
            datasets: [
              {
                name: 'Total',
                values: total_expenses,
                color: '#ef4444' // Red
              },
              {
                name: 'Fixed',
                values: fixed_expenses,
                color: '#eab308' // Yellow
              },
              {
                name: 'Subscription',
                values: subscription_expenses,
                color: '#3b82f6' // Blue
              },
              {
                name: 'Variable',
                values: variable_expenses,
                color: '#10b981' // Green
              },
              {
                name: 'Installment',
                values: installment_expenses,
                color: '#8b5cf6' // Purple
              }
            ],
            categories: categoryData
          };
          
          setExpenseData(sortedExpenses);
          setTrendData(trendData);
          
          // Set totals using the most recent values (first in each array)
          setTotals({
            total_expenses: total_expenses[0] || 0,
            variable_expense: variable_expenses[0] || 0,
            fixed_expense: fixed_expenses[0] || 0,
            subscription_expense: subscription_expenses[0] || 0,
            installment_expense: installment_expenses[0] || 0,
            value_not_matched: 0,
            monthly_expenses: total_expenses[0] || 0
          });
        } else {
          // Handle the old API response format if needed
          const data: ExpensesResponse = result.data;
          
          const sortedExpenses = [...data.expenses].sort((a, b) => {
            const dateA = new Date(a.date + '-01');
            const dateB = new Date(b.date + '-01');
            return dateB.getTime() - dateA.getTime();
          });
          
          setExpenseData(sortedExpenses);
          setTrendData(data.trend_data || null);
          setTotals({
            total_expenses: data.total_expenses || 0,
            variable_expense: data.variable_expense || 0,
            fixed_expense: data.fixed_expense || 0,
            subscription_expense: data.subscription_expense || 0,
            installment_expense: data.installment_expense || 0,
            value_not_matched: data.value_not_matched || 0,
            monthly_expenses: data.monthly_expenses || 0
          });
        }
      } else {
        throw new Error(result.message || 'Failed to fetch expense data');
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      setError(error instanceof Error ? error.message : 'An error occurred while fetching expense data');
      setExpenseData([]);
      setTrendData(null);
      setTotals({
        total_expenses: 0,
        variable_expense: 0,
        fixed_expense: 0,
        subscription_expense: 0,
        installment_expense: 0,
        value_not_matched: 0,
        monthly_expenses: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    if (mounted) {
      fetchExpenseData();
    }

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  const calculateChanges = (): ExpenseChanges => {
    if (!expenseData || expenseData.length < 2) {
      return {
        total_expenses: 0,
        variable_expense: 0,
        fixed_expense: 0,
        subscription_expense: 0,
        installment_expense: 0
      };
    }
    
    const current = expenseData[0];
    const previous = expenseData[1];
    
    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      total_expenses: calculateChange(current.total_expenses, previous.total_expenses),
      variable_expense: calculateChange(current.variable_expense, previous.variable_expense),
      fixed_expense: calculateChange(current.fixed_expense, previous.fixed_expense),
      subscription_expense: calculateChange(current.subscription_expense, previous.subscription_expense),
      installment_expense: calculateChange(current.installment_expense, previous.installment_expense),
    };
  };

  const calculateYearlyProjection = (): number => {
    if (!expenseData || expenseData.length === 0) return 0;
    return totals.monthly_expenses * 12;
  };

  return {
    expenseData,
    trendData,
    totals,
    isLoading,
    error,
    changes: calculateChanges(),
    yearlyProjection: calculateYearlyProjection(),
    refreshData: fetchExpenseData,
    monthsAvailable: expenseData.length
  };
}